import { useMemo, useState, useRef, useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import type { QuerySpec, QueryResultRow } from '@/types/query';
import { useQueryResult } from '@/hooks/useQueryResult';
import { useDatasetStore } from '@/store/dataset-store';
import { useTableVisibilityStore } from '@/store/table-vis-store';
import { ExcelExportButton } from '@/features/export/ExcelExportButton';
import { WidgetContainer } from './WidgetContainer';
import { ReloadButton } from './ReloadButton';
import { formatDDMMYYYY, pad2 } from '@/lib/date-format';

/**
 * Stable reference for the "no visibility set yet" case. Returning a fresh
 * `{}` from the Zustand selector trips React 18's useSyncExternalStore
 * cached-snapshot check and throws "The result of getSnapshot should be
 * cached to avoid an infinite loop".
 */
const EMPTY_VISIBILITY: VisibilityState = Object.freeze({});

/**
 * Stable reference for "no rows" used by the `useReactTable({ data })`
 * prop. `data ?? []` would mint a fresh empty array on every render —
 * TanStack treats that as a data change, calls `setState` to bump its
 * internal generation, the host re-renders, mints another empty array,
 * and so on. That tight loop is what froze the page on Temizle: the
 * widget's `useQueryResult` flipped `data` to `null` (compare-mode bail
 * path), and the table then re-entered itself ~760×/s until the user
 * toggled out of compare mode. Freezing the array breaks the cycle.
 */
const EMPTY_ROWS: readonly QueryResultRow[] = Object.freeze([]);

type DataTableProps = {
  title: string;
  spec: QuerySpec;
};

const PAGE_SIZE = 25;
const columnHelper = createColumnHelper<QueryResultRow>();

/** Format a value stored in a DATE/TIMESTAMP column as DD.MM.YYYY (+ HH:mm for timestamps). */
function formatDateLike(val: unknown, sqlType: string): string {
  if (val == null) return '—';
  const isTimestamp = sqlType.toUpperCase().startsWith('TIMESTAMP');
  let date: Date;
  if (val instanceof Date) {
    date = val;
  } else if (typeof val === 'number' && Number.isFinite(val)) {
    date = new Date(val);
  } else if (typeof val === 'bigint') {
    date = new Date(Number(val));
  } else {
    return String(val);
  }
  if (Number.isNaN(date.getTime())) return String(val);
  const d = formatDDMMYYYY(date);
  if (!isTimestamp) return d;
  return `${d} ${pad2(date.getUTCHours())}:${pad2(date.getUTCMinutes())}`;
}

function isDateType(sqlType: string): boolean {
  const upper = sqlType.toUpperCase();
  return upper === 'DATE' || upper.startsWith('TIMESTAMP');
}

/**
 * Paginated data table powered by TanStack Table.
 * Column visibility toggle integrated into the title bar.
 */
export function DataTable({ title, spec }: DataTableProps): JSX.Element {
  const { t } = useTranslation();
  const { data, isLoading, error, reload } = useQueryResult(spec);
  const [sorting, setSorting] = useState<SortingState>([]);
  const columnVisibility = useTableVisibilityStore(
    (s) => s.visibility[spec.source] ?? EMPTY_VISIBILITY,
  );
  const setVisibility = useTableVisibilityStore((s) => s.setVisibility);

  const datasetColumns = useDatasetStore(
    (s) => s.datasets.get(spec.source)?.columns,
  );

  const columnTypes = useMemo(() => {
    if (!datasetColumns) return null;
    const map = new Map<string, string>();
    for (const c of datasetColumns) map.set(c.name, c.sqlType);
    return map;
  }, [datasetColumns]);

  const columns = useMemo(() => {
    if (!data || data.rows.length === 0) return [];
    const keys = Object.keys(data.rows[0]!);
    const compare = data.compareMeta;

    // In compare mode, push __period__ to the front and render it with
    // the user-facing labels ("Mart 2025") rather than 'A' / 'B'.
    const ordered = compare
      ? ['__period__', ...keys.filter((k) => k !== '__period__')]
      : keys;

    return ordered.map((key) => {
      if (key === '__period__' && compare) {
        return columnHelper.accessor((row) => row[key], {
          id: key,
          header: t('dataTable.periodColumn'),
          cell: (info) => {
            const v = info.getValue();
            if (v === 'A') return compare.labelA;
            if (v === 'B') return compare.labelB;
            return '—';
          },
        });
      }
      const sqlType = columnTypes?.get(key) ?? '';
      const dateCol = isDateType(sqlType);
      return columnHelper.accessor((row) => row[key], {
        id: key,
        header: key,
        cell: (info) => {
          const val = info.getValue();
          if (dateCol) return formatDateLike(val, sqlType);
          if (val == null) return '—';
          if (typeof val === 'number') return val.toLocaleString();
          return String(val);
        },
      });
    });
  }, [data, columnTypes, t]);

  const table = useReactTable({
    data: (data?.rows ?? EMPTY_ROWS) as QueryResultRow[],
    columns,
    state: { sorting, columnVisibility },
    onSortingChange: setSorting,
    onColumnVisibilityChange: (updater) => {
      const next =
        typeof updater === 'function' ? updater(columnVisibility) : updater;
      setVisibility(spec.source, next);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageIndex: 0, pageSize: PAGE_SIZE } },
  });

  const rows = table.getRowModel().rows;
  const pageIndex = table.getState().pagination.pageIndex;
  const pageCount = table.getPageCount();
  const total = data?.rows.length ?? 0;
  const firstRow = total === 0 ? 0 : pageIndex * PAGE_SIZE + 1;
  const lastRow = Math.min(total, (pageIndex + 1) * PAGE_SIZE);

  const titleBarExtra = (
    <div className="flex items-center gap-1.5">
      <ReloadButton onReload={reload} disabled={isLoading} />
      <ExcelExportButton variant="compact" />
      <ColumnVisibilityDropdown table={table} />
    </div>
  );

  return (
    <WidgetContainer
      title={title}
      isLoading={isLoading}
      error={error}
      draggable={false}
      titleBarExtra={titleBarExtra}
    >
      <div className="flex flex-col h-full">
        <div className="w-full overflow-x-auto flex-1">
          <table
            className="w-full text-sm border-collapse"
            style={{ color: 'var(--color-fg)' }}
          >
            <thead className="sticky top-0 z-10">
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-3 py-1 text-left text-xs font-medium cursor-pointer select-none whitespace-nowrap border-b"
                      style={{
                        background: 'var(--color-surface)',
                        borderColor: 'var(--color-border)',
                        color: 'var(--color-muted)',
                      }}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                      {{ asc: ' ↑', desc: ' ↓' }[
                        header.column.getIsSorted() as string
                      ] ?? ''}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-b">
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-3 py-1 whitespace-nowrap truncate max-w-[200px]"
                      style={{ borderColor: 'var(--color-border)' }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {total === 0 && !isLoading && (
            <div
              className="flex items-center justify-center p-4 text-sm"
              style={{ color: 'var(--color-muted)' }}
            >
              {t('dataTable.noRows')}
            </div>
          )}
          {/* Visible-column recovery banner — when the user has hidden
              every column the table body collapses to a row of empty
              cells, which feels like the data vanished. Surface a one-
              click "Tümünü göster" right inside the empty area, with
              enough vertical breathing room (min-h) that the column-
              visibility dropdown anchored in the title bar always has
              room to open below it. */}
          {table.getVisibleLeafColumns().length === 0 && (
            <div
              className="flex flex-col items-center justify-center gap-3 p-6 text-xs min-h-[280px]"
              style={{ color: 'var(--color-muted)' }}
            >
              <span>{t('dataTable.allColumnsHidden')}</span>
              <button
                type="button"
                onClick={() => table.toggleAllColumnsVisible(true)}
                className="text-xs px-3 py-1 rounded text-white"
                style={{ background: 'var(--color-accent)' }}
              >
                {t('dataTable.showAll')}
              </button>
            </div>
          )}
        </div>

        {total > 0 && (
          <nav
            className="flex items-center justify-between px-3 py-2 text-xs border-t shrink-0"
            style={{
              background: 'var(--color-surface)',
              borderColor: 'var(--color-border)',
              color: 'var(--color-muted)',
            }}
            aria-label={t('dataTable.paginationAria')}
          >
            <span>
              {firstRow.toLocaleString()}–{lastRow.toLocaleString()} / {total.toLocaleString()}
            </span>
            <div className="flex items-center gap-1">
              <PagerButton
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
                label={t('dataTable.firstPage')}
              >
                «
              </PagerButton>
              <PagerButton
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                label={t('dataTable.prevPage')}
              >
                {t('dataTable.prevPageLabel')}
              </PagerButton>
              <span
                className="px-3 py-1 text-sm font-medium"
                style={{ color: 'var(--color-fg)' }}
              >
                {pageIndex + 1} / {Math.max(1, pageCount)}
              </span>
              <PagerButton
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                label={t('dataTable.nextPage')}
              >
                {t('dataTable.nextPageLabel')}
              </PagerButton>
              <PagerButton
                onClick={() => table.setPageIndex(pageCount - 1)}
                disabled={!table.getCanNextPage()}
                label={t('dataTable.lastPage')}
              >
                »
              </PagerButton>
            </div>
          </nav>
        )}
      </div>
    </WidgetContainer>
  );
}

// ---------------------------------------------------------------------------
// Column visibility dropdown — rendered in the title bar
// ---------------------------------------------------------------------------

type ColumnVisibilityDropdownProps = {
  table: ReturnType<typeof useReactTable<QueryResultRow>>;
};

function ColumnVisibilityDropdown({
  table,
}: ColumnVisibilityDropdownProps): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const allColumns = table.getAllLeafColumns();
  const visibleCount = allColumns.filter((c) => c.getIsVisible()).length;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-xs px-2 py-0.5 rounded hover:opacity-80 flex items-center gap-1"
        style={{
          color: 'var(--color-muted)',
          border: '1px solid var(--color-border)',
          background: 'var(--color-bg)',
        }}
        title={t('dataTable.columnVisibility')}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
          <path d="M1.5 3h9M1.5 6h9M1.5 9h9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
        </svg>
        {visibleCount}/{allColumns.length}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-50 rounded-lg shadow-lg max-h-64 overflow-y-auto min-w-[220px] p-2"
          style={{
            background: 'var(--color-bg)',
            border: '1px solid var(--color-border)',
          }}
        >
          {/* Single toggle that flips label + action based on current
              state — matches the classic Excel pivot UX the user asked
              for. When every column is already visible the toggle
              shifts to "Tümünü kaldır" so the same click can shed the
              full selection; otherwise it shows "Tümünü seç" as the
              recovery action. */}
          {(() => {
            const allShown = allColumns.every((c) => c.getIsVisible());
            return (
              <div
                className="mb-1 pb-1 border-b"
                style={{ borderColor: 'var(--color-border)' }}
              >
                <button
                  type="button"
                  onClick={() => table.toggleAllColumnsVisible(!allShown)}
                  className="w-full text-[11px] px-2 py-1 rounded hover:opacity-80"
                  style={{
                    color: 'var(--color-accent)',
                    border: '1px solid var(--color-border)',
                  }}
                >
                  {allShown ? t('dataTable.hideAll') : t('dataTable.showAll')}
                </button>
              </div>
            );
          })()}
          {allColumns.map((column) => (
            <label
              key={column.id}
              className="flex items-center gap-2 px-2 py-0.5 text-xs cursor-pointer rounded hover:opacity-80"
              style={{ color: 'var(--color-fg)' }}
            >
              <input
                type="checkbox"
                checked={column.getIsVisible()}
                onChange={column.getToggleVisibilityHandler()}
              />
              {column.id}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pager button
// ---------------------------------------------------------------------------

type PagerButtonProps = {
  onClick: () => void;
  disabled: boolean;
  label: string;
  children: ReactNode;
};

function PagerButton({
  onClick,
  disabled,
  label,
  children,
}: PagerButtonProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="px-3 py-1.5 text-sm rounded disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-80"
      style={{
        border: '1px solid var(--color-border)',
        background: 'var(--color-bg)',
        color: 'var(--color-fg)',
      }}
    >
      {children}
    </button>
  );
}
